import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  Plus, Edit2, X, Loader2, CheckCircle, Tag,
  Percent, DollarSign, ToggleLeft, ToggleRight,
  AlertCircle, Infinity
} from 'lucide-react';
import { toast } from 'sonner';
import { usePublicSettings } from '../../contexts/PublicSettingsContext';
import { currencySymbol } from '../../lib/currency';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_FORM = {
  code: '',
  description: '',
  discount_type: 'percent',
  discount_value: '',
  max_uses: '',
  valid_from: '',
  valid_until: '',
};

const fmt = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const CouponStatusBadge = ({ coupon }) => {
  const now = new Date();
  const expired = coupon.valid_until && new Date(coupon.valid_until) < now;
  const notYet = coupon.valid_from && new Date(coupon.valid_from) > now;
  const full = coupon.max_uses != null && coupon.uses_count >= coupon.max_uses;

  if (!coupon.is_active) return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#F5F5F5] text-[#1A2E6E]/40">Inactive</span>;
  if (expired) return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#D72B2B]/10 text-[#D72B2B]">Expired</span>;
  if (notYet) return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#F5A623]/10 text-[#F5A623]">Scheduled</span>;
  if (full) return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#D72B2B]/10 text-[#D72B2B]">Limit reached</span>;
  return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-[#4CAF50]/10 text-[#4CAF50]">Active</span>;
};

export const AdminCoupons = () => {
  const { settings } = usePublicSettings();
  const sym = currencySymbol(settings.default_currency || 'EUR');
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  useEffect(() => { fetchCoupons(); }, []);

  const fetchCoupons = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/coupons?page_size=500`);
      setCoupons(res.data.data ?? res.data);
    } catch {
      toast.error('Failed to load coupons');
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditingCoupon(null);
    setForm(EMPTY_FORM);
    setModalOpen(true);
  };

  const openEdit = (coupon) => {
    setEditingCoupon(coupon);
    setForm({
      code: coupon.code,
      description: coupon.description || '',
      discount_type: coupon.discount_type,
      discount_value: coupon.discount_value,
      max_uses: coupon.max_uses ?? '',
      valid_from: coupon.valid_from ? coupon.valid_from.slice(0, 10) : '',
      valid_until: coupon.valid_until ? coupon.valid_until.slice(0, 10) : '',
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.code.trim()) { toast.error('Code is required'); return; }
    if (!form.discount_value || isNaN(parseFloat(form.discount_value)) || parseFloat(form.discount_value) <= 0) {
      toast.error('Discount value must be a positive number');
      return;
    }
    if (form.discount_type === 'percent' && parseFloat(form.discount_value) > 100) {
      toast.error('Percent discount cannot exceed 100%');
      return;
    }

    setSaving(true);
    const payload = {
      code: form.code.toUpperCase().trim(),
      description: form.description || null,
      discount_type: form.discount_type,
      discount_value: parseFloat(form.discount_value),
      max_uses: form.max_uses !== '' ? parseInt(form.max_uses) : null,
      valid_from: form.valid_from || null,
      valid_until: form.valid_until || null,
    };

    try {
      if (editingCoupon) {
        await axios.put(`${API_URL}/api/admin/coupons/${editingCoupon.id}`, payload);
        toast.success('Coupon updated');
      } else {
        await axios.post(`${API_URL}/api/admin/coupons`, payload);
        toast.success('Coupon created');
      }
      setModalOpen(false);
      fetchCoupons();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save coupon');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (coupon) => {
    setTogglingId(coupon.id);
    try {
      if (coupon.is_active) {
        await axios.delete(`${API_URL}/api/admin/coupons/${coupon.id}`);
        toast.success('Coupon deactivated');
      } else {
        await axios.put(`${API_URL}/api/admin/coupons/${coupon.id}`, { is_active: true });
        toast.success('Coupon activated');
      }
      fetchCoupons();
    } catch {
      toast.error('Failed to update coupon');
    } finally {
      setTogglingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 text-[#D72B2B] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-[#1A2E6E] font-['Sora']">Coupons</h1>
          <p className="text-[#1A2E6E]/60 mt-1">Manage discount codes for students and consultants</p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2.5 bg-[#D72B2B] text-white rounded-xl hover:bg-[#B71C1C] transition-colors text-sm font-medium shrink-0"
        >
          <Plus size={16} /> New Coupon
        </button>
      </div>

      {/* Summary bar */}
      {coupons.length > 0 && (
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: 'Total', value: coupons.length, color: 'text-[#1A2E6E]', bg: 'bg-[#1A2E6E]/5' },
            { label: 'Active', value: coupons.filter(c => c.is_active).length, color: 'text-[#4CAF50]', bg: 'bg-[#4CAF50]/8' },
            { label: 'Total Uses', value: coupons.reduce((a, c) => a + (c.uses_count || 0), 0), color: 'text-[#F5A623]', bg: 'bg-[#F5A623]/8' },
          ].map(({ label, value, color, bg }) => (
            <div key={label} className={`${bg} rounded-xl p-4 text-center`}>
              <p className={`text-2xl font-bold ${color}`}>{value}</p>
              <p className="text-xs text-[#1A2E6E]/60 mt-0.5">{label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Coupons list */}
      {coupons.length === 0 ? (
        <div className="bg-white rounded-xl border border-[#E8EAF0] p-12 text-center">
          <Tag className="w-12 h-12 text-[#1A2E6E]/20 mx-auto mb-4" />
          <p className="text-[#1A2E6E]/60">No coupons yet. Create one to offer discounts to students.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-[#E8EAF0] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#E8EAF0] bg-[#F8F9FC]">
                  <th className="text-left px-5 py-3 text-xs font-semibold text-[#1A2E6E]/60 uppercase tracking-wide">Code</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-[#1A2E6E]/60 uppercase tracking-wide">Discount</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-[#1A2E6E]/60 uppercase tracking-wide hidden sm:table-cell">Uses</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-[#1A2E6E]/60 uppercase tracking-wide hidden md:table-cell">Validity</th>
                  <th className="text-left px-5 py-3 text-xs font-semibold text-[#1A2E6E]/60 uppercase tracking-wide">Status</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E8EAF0]">
                {coupons.map((coupon, i) => (
                  <motion.tr
                    key={coupon.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: i * 0.03 }}
                    className={`hover:bg-[#F8F9FC] transition-colors ${!coupon.is_active ? 'opacity-50' : ''}`}
                  >
                    <td className="px-5 py-4">
                      <div>
                        <span className="font-mono font-bold text-[#1A2E6E] tracking-wider">{coupon.code}</span>
                        {coupon.description && (
                          <p className="text-xs text-[#1A2E6E]/50 mt-0.5">{coupon.description}</p>
                        )}
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg font-semibold text-sm ${
                        coupon.discount_type === 'percent'
                          ? 'bg-[#6366F1]/10 text-[#6366F1]'
                          : 'bg-[#4CAF50]/10 text-[#4CAF50]'
                      }`}>
                        {coupon.discount_type === 'percent'
                          ? <><Percent size={13} />{coupon.discount_value}% off</>
                          : <><DollarSign size={13} />{sym}{coupon.discount_value} off</>
                        }
                      </span>
                    </td>
                    <td className="px-5 py-4 hidden sm:table-cell">
                      <span className="text-[#1A2E6E]">
                        {coupon.uses_count}
                        {coupon.max_uses != null
                          ? <span className="text-[#1A2E6E]/40"> / {coupon.max_uses}</span>
                          : <span className="text-[#1A2E6E]/30 ml-1"><Infinity size={13} className="inline" /></span>
                        }
                      </span>
                    </td>
                    <td className="px-5 py-4 hidden md:table-cell text-[#1A2E6E]/60 text-xs">
                      {coupon.valid_from || coupon.valid_until
                        ? <>{fmt(coupon.valid_from)} → {fmt(coupon.valid_until)}</>
                        : <span className="text-[#1A2E6E]/30">No expiry</span>
                      }
                    </td>
                    <td className="px-5 py-4">
                      <CouponStatusBadge coupon={coupon} />
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-1 justify-end">
                        <button
                          onClick={() => openEdit(coupon)}
                          className="p-2 rounded-lg hover:bg-[#1A2E6E]/5 text-[#1A2E6E]/60 hover:text-[#1A2E6E] transition-colors"
                          title="Edit"
                        >
                          <Edit2 size={15} />
                        </button>
                        <button
                          onClick={() => toggleActive(coupon)}
                          disabled={togglingId === coupon.id}
                          title={coupon.is_active ? 'Deactivate' : 'Activate'}
                          className={`p-2 rounded-lg transition-colors ${
                            coupon.is_active
                              ? 'text-[#4CAF50] hover:bg-[#4CAF50]/10'
                              : 'text-[#1A2E6E]/30 hover:bg-[#1A2E6E]/5'
                          }`}
                        >
                          {togglingId === coupon.id
                            ? <Loader2 size={17} className="animate-spin" />
                            : coupon.is_active ? <ToggleRight size={20} /> : <ToggleLeft size={20} />
                          }
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create / Edit Modal */}
      <AnimatePresence>
        {modalOpen && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col"
            >
              <div className="flex items-center justify-between p-6 border-b border-[#E8EAF0]">
                <h2 className="text-lg font-bold text-[#1A2E6E]">
                  {editingCoupon ? 'Edit Coupon' : 'New Coupon'}
                </h2>
                <button onClick={() => setModalOpen(false)} className="p-2 hover:bg-[#1A2E6E]/5 rounded-lg">
                  <X size={20} className="text-[#1A2E6E]/60" />
                </button>
              </div>

              <div className="p-6 overflow-y-auto space-y-4">
                {/* Code */}
                <div>
                  <label className="block text-sm font-medium text-[#1A2E6E] mb-1">Coupon Code *</label>
                  <input
                    type="text"
                    value={form.code}
                    onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
                    placeholder="e.g. WELCOME20"
                    className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none font-mono uppercase tracking-widest"
                  />
                </div>

                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-[#1A2E6E] mb-1">Description <span className="text-[#1A2E6E]/40 font-normal">(optional)</span></label>
                  <input
                    type="text"
                    value={form.description}
                    onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                    placeholder="e.g. New student welcome discount"
                    className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none"
                  />
                </div>

                {/* Discount Type + Value */}
                <div>
                  <label className="block text-sm font-medium text-[#1A2E6E] mb-2">Discount *</label>
                  <div className="flex gap-2">
                    <div className="flex rounded-xl border border-[#D1D5DB] overflow-hidden">
                      {[
                        { key: 'percent', icon: Percent, label: '%' },
                        { key: 'fixed',   icon: DollarSign, label: sym },
                      ].map(({ key, icon: Icon, label }) => (
                        <button
                          key={key}
                          type="button"
                          onClick={() => setForm(f => ({ ...f, discount_type: key }))}
                          className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium transition-colors ${
                            form.discount_type === key
                              ? 'bg-[#1A2E6E] text-white'
                              : 'text-[#1A2E6E]/60 hover:bg-[#F5F7FA]'
                          }`}
                        >
                          <Icon size={14} /> {label}
                        </button>
                      ))}
                    </div>
                    <input
                      type="number"
                      min="0"
                      max={form.discount_type === 'percent' ? 100 : undefined}
                      step="0.01"
                      value={form.discount_value}
                      onChange={e => setForm(f => ({ ...f, discount_value: e.target.value }))}
                      placeholder={form.discount_type === 'percent' ? '20' : '50.00'}
                      className="flex-1 px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none"
                    />
                  </div>
                  <p className="text-xs text-[#1A2E6E]/50 mt-1.5">
                    {form.discount_type === 'percent'
                      ? `Students will pay ${form.discount_value ? (100 - parseFloat(form.discount_value)).toFixed(0) : '?'}% of the original price`
                      : `${sym}${form.discount_value || '0'} will be subtracted from the plan price`
                    }
                  </p>
                </div>

                {/* Max Uses */}
                <div>
                  <label className="block text-sm font-medium text-[#1A2E6E] mb-1">
                    Max Uses <span className="text-[#1A2E6E]/40 font-normal">(leave blank = unlimited)</span>
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={form.max_uses}
                    onChange={e => setForm(f => ({ ...f, max_uses: e.target.value }))}
                    placeholder="e.g. 50"
                    className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none"
                  />
                </div>

                {/* Validity dates */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-[#1A2E6E] mb-1">Valid From</label>
                    <input
                      type="date"
                      value={form.valid_from}
                      onChange={e => setForm(f => ({ ...f, valid_from: e.target.value }))}
                      className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#1A2E6E] mb-1">Valid Until</label>
                    <input
                      type="date"
                      value={form.valid_until}
                      onChange={e => setForm(f => ({ ...f, valid_until: e.target.value }))}
                      className="w-full px-4 py-3 rounded-xl border border-[#D1D5DB] focus:border-[#1A2E6E] outline-none text-sm"
                    />
                  </div>
                </div>
              </div>

              <div className="flex gap-3 p-6 border-t border-[#E8EAF0]">
                <button
                  onClick={() => setModalOpen(false)}
                  className="flex-1 py-3 rounded-xl border border-[#E8EAF0] text-[#1A2E6E] hover:bg-[#F5F7FA] transition-colors font-medium"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1 py-3 rounded-xl bg-[#1A2E6E] text-white hover:bg-[#16275C] transition-colors font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {saving ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
                  {editingCoupon ? 'Save Changes' : 'Create Coupon'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminCoupons;
